// @ts-nocheck TODO

/**
 * @file Types and functions used in the citation panel.
 */

import isEqual from "lodash/isEqual.js";
import moment from "moment";
import Citation, {cslNameTypes} from "../../../main/citation.js";
import {format as formatCSLdate} from "@citation-js/date";
import {CitationPanel, STATES} from "./CitationPanel.js";

import {annotationCitation} from "../../../main/data/model/index.js";

// Formatted citation data passed to the citation information component
export type citationPanelData = annotationCitation & {
	title?:          string;
	creators?:       citationPanelCreator[];
	publisher?:      string;
	date_published?: string;
	date_accessed?:  string;
};

export interface citationPanelCreator {
	creatorType: string;
	given?:      string;
	family?:     string;
	id:          number;
};

const infoRoot = (context: CitationPanel) => {
	return context._citationInformation.shadowRoot;
};

const inputValue = (context: CitationPanel, inputId: string): string => {
	return infoRoot(context)?.querySelector(`#${inputId}`)?.shadowRoot?.querySelector("input")?.value.trim() ?? "";
};

const detailsInputValue = (context: CitationPanel, inputId: string): string => {
	const allInputs = context._citationInformation.shadowRoot?.querySelector("pn-collapsible-section")?.getElementsByTagName("pn-labeled-text-input") ?? [];
	const labledInput = Array.from(allInputs).find(e => e.id === inputId);
	const input = labledInput?.shadowRoot?.querySelector("input");
	return (input)?.value.trim() ?? "";
};

/**
 * Gets the HTML string of what is currently in the citation editor.
 **/
const getFromPanel_RenderedCitation = (context: CitationPanel): string => {
	const citationRTE = infoRoot(context)?.querySelector("pn-citation-editor")?.shadowRoot?.querySelector("pn-rte");
	const rendered_citation = (citationRTE)?.editor?.getHTML();

	return rendered_citation ?? "";
};

/**
 * Gets the value of the currently selected citation format.
 **/
const getFromPanel_CitationFormat = (context: CitationPanel): string => {
	const formatSelect = infoRoot(context)?.querySelector("#citation-format-select");
	return formatSelect?.getSelectedOption()?.value || "";
};

/**
 * Gets the value of the currently selected citation format.
 **/
const getFromPanel_IsRenderModified = (context: CitationPanel): boolean => {
	const citationRTE = infoRoot(context)?.querySelector("pn-citation-editor")?.shadowRoot?.querySelector("pn-rte");
	return citationRTE?.classList.contains("is-render-modified") ?? false;
};

/**
 * Gets the value of the search field.
 **/
export const getFromPanel_SearchValue = (context: CitationPanel) => {
	return inputValue(context, "citation-search-field");
};

/**
 * If the original date was an ISO string, and it refers to the same day as
 * the newer date being checked, returns the original ISO string. Otherwise,
 * returns the newer date as is.
 *
 * @param {string} date         date string
 * @param {string} originalDate originally a csl format date
**/
const getUnformattedDate = (date: string, originalDate: string) => {
	const originalMoment = moment(originalDate);
	if (originalMoment.isValid() && moment(date).isSame(originalMoment, "day"))
		return Citation.getCSLdate(originalDate);
	else
		return Citation.getCSLdate(date);
};

/**
 * Formats a CSL date object to a readable string.
**/
export const getFormattedDate = (date: object): string => {
	if (date === undefined)
		return "";
	else {
		const dateString = formatCSLdate(date);
		return moment(dateString).format("MMMM DD, YYYY");
	}
};

/**
 * Gets the list of creators from the citation panel. Creators that don't
 * have given or family filled out are ignored.
 *
 * @return {Array} Array of creator objects, which contain creatorType,
 *                 given and family
 **/
const getFromPanel_Creators = (context: CitationPanel) => {
	const creators = [];
	const creatorsList = context._citationInformation.shadowRoot?.querySelector("pn-collapsible-section")?.querySelector("#citation-contributors-list")?.children ?? [];

	for (const element of creatorsList) {
		const select = element.querySelector("pn-select")!;
		const type = select?.getSelectedOption()?.value;
		const given = element.querySelector(".citation-contributor-given")?.shadowRoot?.querySelector("input")?.value.trim();
		const family = element.querySelector(".citation-contributor-family")?.shadowRoot?.querySelector("input")?.value.trim();

		// Add the creator as long as both first and last name fields aren't empty
		if (given !== "" || family !== "")
			creators.push({type, given, family});
	}

	// One day, Array.prototype.group():
	// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/group
	const people = creators.reduce((peopleGroups, person) => {
		// Remove type from object being added to grouped array
		const {type, ...rest} = person;
		const curGroup = peopleGroups[type] ?? [];

		return {...peopleGroups, [type]: [...curGroup, {...rest}]};
	}, {});

	return people;
};

/**
 * Gets the list of source notes from the citation panel.
 *
 * @return {Array} Array of source note objects, which contain label and note
 **/
const getFromPanel_SourceNotes = (context: CitationPanel) => {
	const sourceNotes = [];
	const sourceNotesList = context.shadowRoot?.querySelector("pn-citation-notes")?.shadowRoot?.querySelectorAll("#citation-notes-list .citation-source-note") ?? [];

	for (let i = 0; i < sourceNotesList.length; i++) {
		const element = sourceNotesList[i];
		const label = element.querySelector("input")?.value.trim();
		const note = element.querySelector("textarea")?.value.trim();

		sourceNotes.push({label, note, position: i});
	}

	return sourceNotes;
};

/**
 * Gets the value of the pages field.
 **/
const getFromPanel_Pages = (context: CitationPanel): string | undefined => {
	let pages: string | undefined = inputValue(context, "citation-field-pages");
	if (pages === "")
		pages = undefined;

	return pages;
};

/**
 * Gets the values of individual fields in the citation panel and places
 * them in an object depending on the citation's type.
 *
 * @return {Object} Data that would go in citation.data.csl
 **/
export const getFromPanel_IndividualFieldsCslData = (context: CitationPanel) => {
	const origCslData = context.citation.data.csl;

	// This is an object of people types, which contain arrays of people objects
	const creators = getFromPanel_Creators(context);

	// Accessed date
	const accessed = getUnformattedDate(detailsInputValue(context, "citation-field-date-accessed"), getFormattedDate(origCslData.accessed));

	// Published date
	const issued = getUnformattedDate(detailsInputValue(context, "citation-field-date-published"), getFormattedDate(origCslData.date));

	const publisher = detailsInputValue(context, "citation-field-publisher");
	const title = detailsInputValue(context, "citation-field-title");

	const type = origCslData.type;

	let returnedData = {
		...(shouldAddToCslData(origCslData.title, title) && {title}),
		...(shouldAddToCslData(origCslData.accessed, accessed) && {accessed}),
		...(shouldAddToCslData(origCslData.date, issued) && {issued}),
		...(shouldAddToCslData(origCslData.publisher, publisher) && {publisher})
		// we purposely exclude "pages" because it causes problems with bulk-update
	};

	// Add people
	for (const [personType, peopleArr] of Object.entries(creators)) {
		returnedData = {
			...returnedData,
			...(shouldAddToCslData(origCslData[personType], peopleArr) && {[personType]: peopleArr})
		};
	}

	// Set fields based on csl type
	switch (type) {
		case "article-journal":
		case "book":
		case "entry-encyclopedia":
		case "webpage":
			return returnedData;

		default:
			return {
				type: "webpage", // in case there is no type
				...returnedData
			};
	}
};

/**
 * Determines whether newData should be used in the updated csl data
 **/
const shouldAddToCslData = (oldData: string | object | undefined, newData: string | object | undefined): boolean => {
	switch (typeof newData) {
		case "string":
			if (oldData === undefined)
				return newData !== "";

			return true;

		case "object":
			if (newData === null)
				return false;

			if (oldData === undefined) {
				if (Array.isArray(newData))
					return newData.length !== 0;
				else
					return Object.entries(newData).length !== 0;
			}

			return true;

		case "undefined":
			return false;

		default:
			return true;
	}
};

/**
 * Gets the updated citation object depending on the state of the citation
 * panel, and the values currently in its fields.
 **/
export const getUpdatedCitation = async (context: CitationPanel): Promise<object> => {
	// Get the data needed to send to this.generateCitation()
	const rendered_citation = getFromPanel_RenderedCitation(context);
	let updateData = {rendered_citation};

	if (context.state === STATES.STANDARD) {
		updateData = {
			...updateData,
			...getFromPanel_IndividualFieldsCslData(context)
		};
	}

	// Generate the updated citation
	let updatedCitation = await generateCitation(context, updateData);

	// Add or remove search_value from the updated citation depending on
	// the citation panel state
	if (context.state === STATES.SEARCH) {
		updatedCitation = {
			...updatedCitation,
			search_value: getFromPanel_SearchValue(context)
		};
	} else if (updatedCitation.search_value)
		// Delete search_value so that the next time this citation is opened,
		// it doesn't open in the search state
		delete updatedCitation.search_value;

	// Remaining things to add to the updated citation data
	const pages = getFromPanel_Pages(context);
	const source_notes = getFromPanel_SourceNotes(context);
	const approvedDt = context.citation.approved
		? context.citation.approve_dt
		: moment().toISOString();

	return {
		...updatedCitation,
		...(pages ? {pages} : undefined),
		source_notes,
		approved:   rendered_citation !== "<p></p>",
		approve_dt: approvedDt
	};
};

/**
 * Applies the given changes to the citation, and returns the new modified object.
 *
 * If the citation is structured, the keys should match the csl format (annotation.citation.data.csl[*]),
 * except for "rendered_citation" (HTML string), if overriding the rendered citation.
 *
 * @param {Object} data
 */
export const generateCitation = async (context: CitationPanel, changes) => {
	const citation = Object.assign({}, context.annotation.citation);

	let citationChanges = {};

	let data_modified = context.citation.data_modified;
	if (citation.structured) {
		if (changes.rendered_citation !== undefined) {
			citationChanges.rendered_citation = changes.rendered_citation;
			delete changes.rendered_citation;
		}

		citation.data
			// TODO
			// this.citationExtExists
			// ? (await this.context.sendMessageToMainFrame.getCitationFromExtension()).data :
			= Citation.updateDataManually(citation.data, {csl: changes});

		if (!context.citation.data_modified)
			data_modified = !isEqual(context.citation.data.csl, citation.data.csl);
	} else {
		citationChanges = changes;
		const oldCit = context.citation.rendered_citation;
		const newCit = changes.rendered_citation;
		// On the first save, the RTE adds p-tags around the citation,
		// but we shouldn't consider that a modification.
		const wrappedOldCit = "<p>" + oldCit + "</p>";
		data_modified = !isEqual(oldCit, newCit) && !isEqual(wrappedOldCit, newCit);
	}

	Object.assign(citation, {
		...citationChanges,
		data_modified,
		render_format:   getFromPanel_CitationFormat(context),
		render_modified: getFromPanel_IsRenderModified(context)
	});

	return citation;
};

export const getFormattedCitationData = (context: CitationPanel) => {
	const citation = context.citation ?? {};
	const cslData = citation?.data?.csl ?? {};

	// Get names from citation data
	const creators = [];
	for (const {value} of cslNameTypes) {
		const arr = cslData[value];
		if (arr) {
			creators.push(...arr.map(name => {
				return {
					creatorType: value,
					given:       name.given,
					family:      name.family
				};
			}));
		}
	}

	// Add a placeholder name field if there were none in the citation data
	if (creators.length === 0) {
		creators.push({
			creatorType: "author",
			given:       "",
			family:      ""
		});
	}

	// Set up default source note fields if needed
	let sourceNotes = citation.source_notes;
	if (!sourceNotes) {
		const defaultLabels = (context.project.settings?.source_notes_default_labels)
			? context.project.settings.source_notes_default_labels
			: [];

		sourceNotes = defaultLabels.map(label => {
			return {label, note: ""};
		});
	}

	if (sourceNotes.length === 0) {
		sourceNotes.push({
			label: "Source Note",
			note:  ""
		});
	}

	const search_value = (citation.search_value ?? cslData.DOI) || cslData.ISBN || cslData.PMID || "";

	return Object.assign({}, {
		...citation,
		creators,
		search_value,
		date_accessed:  getFormattedDate(cslData.accessed),
		date_published: getFormattedDate(cslData.issued),
		pages:          (citation.pages ?? cslData.page) || "",
		publisher:      cslData.publisher || "",
		source_notes:   sourceNotes,
		title:          cslData.title || "",
		url:            cslData.URL || context.annotation?.pageData?.pageUrl || "" // TODO
	});
};
